import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, Search, AlertCircle, Phone, Mail, User, Tag, List, Briefcase, Ticket as TicketIcon, FileText, UserCog, MoreVertical, Smile, Paperclip, Mic, X, Filter } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { contactApi } from '../lib/api';
import type { Tables } from '../lib/types';
import RangeSlider from '../components/RangeSlider';

interface Conversation {
  id: string;
  contact: Tables<'contacts'>;
  lastMessage?: string;
  lastMessageTime?: string;
  unread: boolean;
}

const SendMessages = () => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState<Tables<'contacts'>[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showContactInfo, setShowContactInfo] = useState(true);
  const [selectedGender, setSelectedGender] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('attivo');
  const [ageRange, setAgeRange] = useState<[number, number]>([18, 120]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      setLoading(true);
      setError(null);
      const contactsData = await contactApi.getAll();
      setContacts(contactsData);

      const convos: Conversation[] = contactsData.map(contact => ({
        id: contact.id,
        contact,
        unread: false
      }));
      setConversations(convos);
    } catch (error: any) {
      console.error('Error loading contacts:', error);
      setError(error.message || 'Errore nel caricamento dei contatti');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (birthdate: string) => {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleSendMessage = () => {
    if (!selectedConversation || !message.trim()) return;

    const phoneNumber = selectedConversation.contact.phone.replace(/\D/g, '');
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');

    setMessage('');
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.contact.phone.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesGender = selectedGender === 'all' || conv.contact.gender === selectedGender;

    const matchesStatus = selectedStatus === 'tutti' || conv.contact.contact_status === selectedStatus;

    const age = calculateAge(conv.contact.birthdate);
    const matchesAge = age >= ageRange[0] && age <= ageRange[1];

    return matchesSearch && matchesGender && matchesStatus && matchesAge;
  });

  const areAllSelected = filteredConversations.length > 0 &&
    filteredConversations.every(conv => selectedContacts.has(conv.id));

  const handleSendMessageToSelected = () => {
    if (selectedContacts.size === 0 || !message.trim()) return;

    const selectedConvos = filteredConversations.filter(conv =>
      selectedContacts.has(conv.id)
    );

    selectedConvos.forEach(conv => {
      const phoneNumber = conv.contact.phone.replace(/\D/g, '');
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
    });

    setMessage('');
    setSelectedContacts(new Set());
  };

  const toggleContactSelection = (contactId: string) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContacts(newSelected);
  };

  const toggleSelectAll = () => {
    if (areAllSelected) {
      setSelectedContacts(new Set());
    } else {
      const allIds = filteredConversations.map(conv => conv.id);
      setSelectedContacts(new Set(allIds));
    }
  };

  const getGenderLabel = (gender: string) => {
    switch (gender) {
      case 'uomo_etero': return 'Uomo Etero';
      case 'donna_etero': return 'Donna Etero';
      case 'uomo_gay': return 'Uomo Gay';
      case 'donna_gay': return 'Donna Gay';
      default: return 'Non dichiarato';
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-8 pb-4">
        <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm font-medium">Totale Contatti</p>
                    <p className="text-3xl font-bold text-white mt-1">{contacts.length}</p>
                  </div>
                  <div className="bg-white/20 p-3 rounded-lg">
                    <User className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>

              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm font-medium">Conversazioni</p>
                    <p className="text-3xl font-bold text-white mt-1">{filteredConversations.length}</p>
                  </div>
                  <div className="bg-white/20 p-3 rounded-lg">
                    <MessageSquare className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>

              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm font-medium">Selezionata</p>
                    <p className="text-3xl font-bold text-white mt-1">{selectedConversation ? '1' : '0'}</p>
                  </div>
                  <div className="bg-white/20 p-3 rounded-lg">
                    <Send className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden px-8 pb-8 pt-4">
        <div className="w-96 bg-white border-r border-gray-200 flex flex-col rounded-l-xl shadow-lg">
          <div className="border-b border-gray-200">
            <div className="p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cerca conversazione..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-[#00a884] focus:bg-white"
                />
              </div>
            </div>

            <div className="px-3 pb-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Filtri</span>
                </div>
                <span className="text-xs text-gray-500">
                  {selectedGender !== 'all' || selectedStatus !== 'attivo' || ageRange[0] !== 18 || ageRange[1] !== 120 ? 'Attivi' : ''}
                </span>
              </button>

              {showFilters && (
                <div className="mt-3 space-y-4 p-3 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Stato
                    </label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#00a884] focus:border-transparent"
                    >
                      <option value="tutti">Tutti</option>
                      <option value="attivo">Attivi</option>
                      <option value="da_ricontattare">Da Controllare</option>
                      <option value="non_interessato">Non Interessati</option>
                      <option value="blacklist">Blacklist</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Sesso
                    </label>
                    <select
                      value={selectedGender}
                      onChange={(e) => setSelectedGender(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#00a884] focus:border-transparent"
                    >
                      <option value="all">Tutti</option>
                      <option value="uomo_etero">Uomo Etero</option>
                      <option value="donna_etero">Donna Etero</option>
                      <option value="uomo_gay">Uomo Gay</option>
                      <option value="donna_gay">Donna Gay</option>
                      <option value="non_dichiarato">Non Dichiarato</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-3">
                      Fascia d'Età
                    </label>
                    <RangeSlider
                      min={18}
                      max={120}
                      value={ageRange}
                      onChange={setAgeRange}
                    />
                  </div>

                  <button
                    onClick={() => {
                      setSelectedStatus('attivo');
                      setSelectedGender('all');
                      setAgeRange([18, 120]);
                    }}
                    className="w-full px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg transition-colors"
                  >
                    Resetta Filtri
                  </button>
                </div>
              )}
            </div>

            {filteredConversations.length > 0 && (
              <div className="px-3 pb-3">
                <button
                  onClick={toggleSelectAll}
                  className="w-full px-3 py-2 text-sm font-medium text-white bg-[#00a884] hover:bg-[#008f6f] rounded-lg transition-colors"
                >
                  {areAllSelected ? 'Deseleziona Tutti' : 'Seleziona Tutti'}
                  {selectedContacts.size > 0 && ` (${selectedContacts.size})`}
                </button>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500">Caricamento...</div>
              </div>
            ) : filteredConversations.length > 0 ? (
              filteredConversations.map((conv) => (
                <div
                  key={conv.id}
                  className={`w-full p-3 flex items-center gap-3 hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                    selectedConversation?.id === conv.id ? 'bg-gray-100' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedContacts.has(conv.id)}
                    onChange={() => toggleContactSelection(conv.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-5 h-5 text-[#00a884] border-gray-300 rounded focus:ring-[#00a884] cursor-pointer flex-shrink-0"
                  />
                  <button
                    onClick={() => setSelectedConversation(conv)}
                    className="flex items-center gap-3 flex-1 min-w-0"
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-semibold">
                        {getInitials(conv.contact.name)}
                      </span>
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-gray-900 truncate">{conv.contact.name}</p>
                        {conv.lastMessageTime && (
                          <span className="text-xs text-gray-500">{conv.lastMessageTime}</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        {conv.lastMessage || conv.contact.phone}
                      </p>
                    </div>
                  </button>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 p-6 text-center">
                <MessageSquare className="h-12 w-12 mb-3 text-gray-400" />
                <p>Nessuna conversazione trovata</p>
              </div>
            )}
          </div>
        </div>

        {selectedConversation ? (
          <>
            <div className="flex-1 flex flex-col bg-[#efeae2] shadow-lg">
              <div className="flex-1 overflow-y-auto p-6" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h100v100H0z\' fill=\'%23efeae2\'/%3E%3Cpath d=\'M20 30l5-5m10 0l5 5m10-5l5 5m10-5l5 5m10-5l5 5\' stroke=\'%23d9d9d9\' stroke-width=\'.5\' fill=\'none\'/%3E%3C/svg%3E")' }}>
                <div className="max-w-4xl mx-auto">
                  <div className="text-center mb-6">
                    <div className="inline-block bg-white/50 px-4 py-2 rounded-lg shadow-sm">
                      <p className="text-xs text-gray-600">Inizia una conversazione con {selectedConversation.contact.name}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-3 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                    <Smile className="h-6 w-6 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                    <Paperclip className="h-6 w-6 text-gray-600" />
                  </button>
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder={selectedContacts.size > 0 ? `Scrivi un messaggio a ${selectedContacts.size} contatti` : "Scrivi un messaggio"}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (selectedContacts.size > 0) {
                            handleSendMessageToSelected();
                          } else {
                            handleSendMessage();
                          }
                        }
                      }}
                      className="w-full px-4 py-2 bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-[#00a884] focus:bg-white"
                    />
                  </div>
                  {message.trim() ? (
                    <button
                      onClick={selectedContacts.size > 0 ? handleSendMessageToSelected : handleSendMessage}
                      className="p-2 bg-[#00a884] hover:bg-[#008f6f] rounded-full transition-colors"
                      title={selectedContacts.size > 0 ? `Invia a ${selectedContacts.size} contatti` : 'Invia messaggio'}
                    >
                      <Send className="h-5 w-5 text-white" />
                    </button>
                  ) : (
                    <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                      <Mic className="h-6 w-6 text-gray-600" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {showContactInfo && (
              <div className="w-96 bg-white border-l border-gray-200 overflow-y-auto rounded-r-xl shadow-lg">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-semibold text-gray-900">Informazioni Contatto</h3>
                    <button
                      onClick={() => setShowContactInfo(false)}
                      className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <X className="h-5 w-5 text-gray-600" />
                    </button>
                  </div>

                  <div className="flex flex-col items-center mb-6">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center mb-3">
                      <span className="text-white text-3xl font-semibold">
                        {getInitials(selectedConversation.contact.name)}
                      </span>
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">{selectedConversation.contact.name}</h2>
                    <p className="text-sm text-gray-500">{selectedConversation.contact.phone}</p>
                  </div>

                  <div className="space-y-4">
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center gap-3 mb-3">
                        <User className="h-5 w-5 text-gray-500" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Nome</p>
                          <p className="text-sm font-medium text-gray-900">{selectedConversation.contact.name}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mb-3">
                        <Phone className="h-5 w-5 text-gray-500" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Telefono</p>
                          <p className="text-sm font-medium text-gray-900">{selectedConversation.contact.phone}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mb-3">
                        <Mail className="h-5 w-5 text-gray-500" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Email</p>
                          <p className="text-sm font-medium text-gray-900">{selectedConversation.contact.email}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mb-3">
                        <User className="h-5 w-5 text-gray-500" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Sesso</p>
                          <p className="text-sm font-medium text-gray-900">
                            {getGenderLabel(selectedConversation.contact.gender)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mb-3">
                        <User className="h-5 w-5 text-gray-500" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Età</p>
                          <p className="text-sm font-medium text-gray-900">
                            {calculateAge(selectedConversation.contact.birthdate)} anni
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">TAG</span>
                        </div>
                        <button className="text-xs text-[#00a884] hover:text-[#008f6f] font-medium">
                          + Aggiungi
                        </button>
                      </div>
                      <p className="text-sm text-gray-500 italic">No tags</p>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <List className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">LISTE</span>
                        </div>
                        <button className="text-xs text-[#00a884] hover:text-[#008f6f] font-medium">
                          + Aggiungi
                        </button>
                      </div>
                      <p className="text-sm text-gray-500 italic">No lists</p>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">BUSINESS DEALS</span>
                        </div>
                        <button className="text-xs text-[#00a884] hover:text-[#008f6f] font-medium">
                          + Aggiungi
                        </button>
                      </div>
                      <p className="text-sm text-gray-500 italic">No deals</p>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <TicketIcon className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">TICKET</span>
                        </div>
                        <button className="text-xs text-[#00a884] hover:text-[#008f6f] font-medium">
                          + Aggiungi
                        </button>
                      </div>
                      <p className="text-sm text-gray-500 italic">No tickets</p>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">NOTA CONTATTO</span>
                        </div>
                      </div>
                      {selectedConversation.contact.notes ? (
                        <p className="text-sm text-gray-700">{selectedConversation.contact.notes}</p>
                      ) : (
                        <p className="text-sm text-gray-500 italic">Non impostato</p>
                      )}
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <UserCog className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">OPERATORE</span>
                        </div>
                        <button className="text-xs text-[#00a884] hover:text-[#008f6f] font-medium">
                          + Aggiungi
                        </button>
                      </div>
                      <p className="text-sm text-gray-500 italic">No operators</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : selectedContacts.size > 0 ? (
          <div className="flex-1 flex flex-col bg-[#efeae2] shadow-lg rounded-r-xl">
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center max-w-md">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center mx-auto mb-6">
                  <Send className="h-16 w-16 text-white" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-700 mb-2">Invio Multiplo</h2>
                <p className="text-gray-500 mb-4">Hai selezionato {selectedContacts.size} contatt{selectedContacts.size === 1 ? 'o' : 'i'}</p>
                <div className="bg-white/50 backdrop-blur-sm rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600">
                    Scrivi un messaggio nel campo sottostante per inviarlo a tutti i contatti selezionati via WhatsApp
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-3 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <Smile className="h-6 w-6 text-gray-600" />
                </button>
                <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <Paperclip className="h-6 w-6 text-gray-600" />
                </button>
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder={`Scrivi un messaggio a ${selectedContacts.size} contatt${selectedContacts.size === 1 ? 'o' : 'i'}`}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessageToSelected();
                      }
                    }}
                    className="w-full px-4 py-2 bg-gray-100 border-0 rounded-lg focus:ring-2 focus:ring-[#00a884] focus:bg-white"
                  />
                </div>
                {message.trim() ? (
                  <button
                    onClick={handleSendMessageToSelected}
                    className="p-2 bg-[#00a884] hover:bg-[#008f6f] rounded-full transition-colors"
                    title={`Invia a ${selectedContacts.size} contatt${selectedContacts.size === 1 ? 'o' : 'i'}`}
                  >
                    <Send className="h-5 w-5 text-white" />
                  </button>
                ) : (
                  <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                    <Mic className="h-6 w-6 text-gray-600" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-[#efeae2] rounded-r-xl shadow-lg">
            <div className="text-center">
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center mx-auto mb-6">
                <MessageSquare className="h-16 w-16 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-700 mb-2">Benvenuto in Chat WhatsApp</h2>
              <p className="text-gray-500">Seleziona una conversazione per iniziare a chattare o seleziona più contatti per inviare un messaggio multiplo</p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-50 text-red-600 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 max-w-md">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}
    </div>
  );
};

export default SendMessages;
